import { api, SellPage, SellTemplate } from '../api/client';

interface SellState {
  templates: SellTemplate[];
  pages: SellPage[];
  loading: boolean;
  saving: boolean;
  uploadingImage: boolean;
  editingPageId: number | null;
  error: string | null;
}

const state: SellState = {
  templates: [],
  pages: [],
  loading: false,
  saving: false,
  uploadingImage: false,
  editingPageId: null,
  error: null,
};

export async function renderSellPages(container: HTMLElement) {
  container.innerHTML = `
    <div class="card">
      <h2 class="card-header">Create a Sell Page</h2>

      <div id="sell-error" class="alert alert-error" style="display:none;"></div>

      <div class="sell-grid">
        <form id="sell-form" class="sell-form">
          <h3 id="sell-form-title">Create / Update Page</h3>
          <div class="form-group">
            <label class="form-label" for="sell-domain">Domain</label>
            <input id="sell-domain" class="form-input" placeholder="example.com" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="sell-price">Price (USD)</label>
            <input id="sell-price" type="number" class="form-input" min="0" step="1" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="sell-email">Contact Email</label>
            <input id="sell-email" type="email" class="form-input" placeholder="you@example.com" required />
          </div>
          <div class="form-group">
            <label class="form-label" for="sell-template">Template</label>
            <select id="sell-template" class="form-input"></select>
          </div>
          <div class="form-group">
            <label class="form-label" for="sell-status">Status</label>
            <select id="sell-status" class="form-input">
              <option value="active">Active</option>
              <option value="draft">Draft</option>
            </select>
          </div>
          <div class="form-group sell-checkbox-row">
            <label class="form-label" for="sell-show-stats-image">Show Stats Screenshot</label>
            <input id="sell-show-stats-image" type="checkbox" />
          </div>
          <div id="sell-image-upload-wrap" class="form-group" style="display:none;">
            <label class="form-label" for="sell-stats-image">Stats Screenshot (PNG/JPG/WEBP/GIF)</label>
            <input id="sell-stats-image" type="file" class="form-input" accept="image/png,image/jpeg,image/webp,image/gif" />
          </div>
          <div id="sell-upload-loader" class="inline-loader" style="display:none;">
            <div class="spinner spinner-sm"></div>
            <span>Uploading image...</span>
          </div>
          <div class="sell-form-actions">
            <button id="sell-submit-btn" class="btn btn-primary" type="submit">Publish Sell Page</button>
            <button id="sell-cancel-edit-btn" class="btn btn-secondary" type="button" style="display:none;">Cancel Edit</button>
          </div>
        </form>

        <div class="sell-templates">
          <h3>Templates</h3>
          <div id="sell-template-cards" class="template-grid"></div>
        </div>
      </div>

      <div class="sell-pages">
        <h3>Your Sell Pages</h3>
        <div id="sell-pages-list" class="domain-grid"></div>
      </div>
    </div>
  `;

  await loadSellData(container);
  bindSellForm(container);
}

async function loadSellData(container: HTMLElement) {
  const errorDiv = container.querySelector('#sell-error') as HTMLElement;
  errorDiv.style.display = 'none';
  state.loading = true;

  const [templatesRes, pagesRes] = await Promise.all([
    api.listSellTemplates(),
    api.listSellPages(),
  ]);

  if (!templatesRes.success) {
    showError(errorDiv, templatesRes.error || 'Failed to load templates');
  } else {
    state.templates = templatesRes.data || [];
  }

  if (!pagesRes.success) {
    showError(errorDiv, pagesRes.error || 'Failed to load sell pages');
  } else {
    state.pages = pagesRes.data || [];
  }

  renderTemplateOptions(container);
  renderTemplateCards(container);
  renderSellPagesList(container);

  state.loading = false;
}

function bindSellForm(container: HTMLElement) {
  const form = container.querySelector('#sell-form') as HTMLFormElement;
  const errorDiv = container.querySelector('#sell-error') as HTMLElement;
  const showStatsImage = container.querySelector('#sell-show-stats-image') as HTMLInputElement;
  const imageUploadWrap = container.querySelector('#sell-image-upload-wrap') as HTMLElement;
  const uploadLoader = container.querySelector('#sell-upload-loader') as HTMLElement;
  const submitBtn = container.querySelector('#sell-submit-btn') as HTMLButtonElement;
  const cancelEditBtn = container.querySelector('#sell-cancel-edit-btn') as HTMLButtonElement;
  const formTitle = container.querySelector('#sell-form-title') as HTMLElement;

  showStatsImage.addEventListener('change', () => {
    imageUploadWrap.style.display = showStatsImage.checked ? 'block' : 'none';
  });

  cancelEditBtn.addEventListener('click', () => {
    resetSellForm(form, container);
    formTitle.textContent = 'Create / Update Page';
    cancelEditBtn.style.display = 'none';
    submitBtn.textContent = 'Publish Sell Page';
    state.editingPageId = null;
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    errorDiv.style.display = 'none';
    state.saving = true;
    submitBtn.disabled = true;
    submitBtn.textContent = state.editingPageId == null ? 'Publishing...' : 'Saving...';

    const domain = (container.querySelector('#sell-domain') as HTMLInputElement).value.trim().toLowerCase();
    const price = Number((container.querySelector('#sell-price') as HTMLInputElement).value);
    const email = (container.querySelector('#sell-email') as HTMLInputElement).value.trim();
    const templateId = (container.querySelector('#sell-template') as HTMLSelectElement).value;
    const status = (container.querySelector('#sell-status') as HTMLSelectElement).value as 'active' | 'draft';
    const imageInput = container.querySelector('#sell-stats-image') as HTMLInputElement;
    const existingPage = state.pages.find((p) => p.domain_name === domain);

    let statsImageKey: string | null = null;
    if (showStatsImage.checked) {
      const file = imageInput.files?.[0] ?? null;
      if (!file && existingPage?.show_stats_image === 1 && existingPage.stats_image_key) {
        statsImageKey = existingPage.stats_image_key;
      } else if (!file) {
        showError(errorDiv, 'Select a stats screenshot or disable the image option');
        state.saving = false;
        submitBtn.disabled = false;
        submitBtn.textContent = state.editingPageId == null ? 'Publish Sell Page' : 'Save Changes';
        return;
      } else {
        state.uploadingImage = true;
        uploadLoader.style.display = 'inline-flex';
        const uploadResult = await api.uploadSellStatsImage(file);
        state.uploadingImage = false;
        uploadLoader.style.display = 'none';
        if (!uploadResult.success || !uploadResult.data) {
          showError(errorDiv, uploadResult.error || 'Failed to upload screenshot');
          state.saving = false;
          submitBtn.disabled = false;
          submitBtn.textContent = state.editingPageId == null ? 'Publish Sell Page' : 'Save Changes';
          return;
        }
        statsImageKey = uploadResult.data.key;
      }
    }

    const payload = {
      domain_name: domain,
      price,
      contact_email: email,
      template_id: templateId,
      status,
      show_stats_image: showStatsImage.checked,
      stats_image_key: statsImageKey,
    };

    const result = state.editingPageId == null
      ? await api.createSellPage(payload)
      : await api.updateSellPage(state.editingPageId, payload);

    if (!result.success || !result.data) {
      showError(errorDiv, result.error || 'Failed to save sell page');
      state.saving = false;
      submitBtn.disabled = false;
      submitBtn.textContent = state.editingPageId == null ? 'Publish Sell Page' : 'Save Changes';
      return;
    }

    state.pages = [result.data, ...state.pages.filter((p) => p.id !== result.data?.id)];
    renderSellPagesList(container);
    resetSellForm(form, container);
    formTitle.textContent = 'Create / Update Page';
    cancelEditBtn.style.display = 'none';
    state.editingPageId = null;
    state.saving = false;
    submitBtn.disabled = false;
    submitBtn.textContent = 'Publish Sell Page';
  });
}

function renderTemplateOptions(container: HTMLElement) {
  const select = container.querySelector('#sell-template') as HTMLSelectElement;
  select.innerHTML = state.templates
    .map((t) => `<option value="${t.id}">${t.name}</option>`)
    .join('');
}

function renderTemplateCards(container: HTMLElement) {
  const wrapper = container.querySelector('#sell-template-cards') as HTMLElement;
  wrapper.innerHTML = state.templates
    .map((t) => `
      <div class="template-card">
        <div class="template-card__title">${t.name}</div>
        <p>${t.description}</p>
      </div>
    `)
    .join('');
}

function renderSellPagesList(container: HTMLElement) {
  const wrapper = container.querySelector('#sell-pages-list') as HTMLElement;
  if (state.pages.length === 0) {
    wrapper.innerHTML = `<div class="empty-state"><p>No sell pages yet.</p></div>`;
    return;
  }

  wrapper.innerHTML = state.pages
    .map((page) => `
      <article class="domain-card">
        <div class="domain-card__top">
          <div class="domain-card__title">
            <span class="domain-name">${page.domain_name.split('.')[0]}</span><span class="domain-tld">.${page.domain_name.split('.').pop()}</span>
          </div>
          <span class="listing-type buy-now">${page.status}</span>
        </div>
        <div class="domain-card__price">
          <span class="price-label">Price</span>
          <span class="price-value">$${page.price.toLocaleString()}</span>
        </div>
        <div class="domain-card__meta">
          <div>
            <span class="meta-label">Template</span>
            <span class="meta-value">${page.template_id}</span>
          </div>
          <div>
            <span class="meta-label">Contact</span>
            <span class="meta-value">${page.contact_email}</span>
          </div>
          <div>
            <span class="meta-label">Stats Image</span>
            <span class="meta-value">${page.show_stats_image === 1 ? 'Enabled' : 'Disabled'}</span>
          </div>
        </div>
        <div class="domain-card__actions">
          <a class="btn btn-secondary" href="/sell/${page.domain_name}" target="_blank" rel="noopener">Preview</a>
          <button class="btn btn-secondary sell-edit-btn" data-page-id="${page.id}">Edit</button>
          <button class="btn btn-secondary sell-delete-btn" data-page-id="${page.id}">Delete</button>
        </div>
      </article>
    `)
    .join('');

  wrapper.querySelectorAll('.sell-edit-btn').forEach((button) => {
    button.addEventListener('click', () => {
      const id = Number((button as HTMLButtonElement).dataset.pageId);
      const page = state.pages.find((p) => p.id === id);
      if (!page) return;
      populateSellFormForEdit(page);
    });
  });

  wrapper.querySelectorAll('.sell-delete-btn').forEach((button) => {
    button.addEventListener('click', async () => {
      const id = Number((button as HTMLButtonElement).dataset.pageId);
      if (!Number.isInteger(id)) return;
      const confirmed = window.confirm('Delete this sell page?');
      if (!confirmed) return;

      const result = await api.deleteSellPage(id);
      if (!result.success) {
        const errorDiv = document.getElementById('sell-error') as HTMLElement | null;
        if (errorDiv) {
          showError(errorDiv, result.error || 'Failed to delete sell page');
        }
        return;
      }

      state.pages = state.pages.filter((p) => p.id !== id);
      renderSellPagesList(container);

      if (state.editingPageId === id) {
        const form = document.getElementById('sell-form') as HTMLFormElement | null;
        if (form) {
          resetSellForm(form, container);
        }
        const formTitle = document.getElementById('sell-form-title') as HTMLElement | null;
        const cancelEditBtn = document.getElementById('sell-cancel-edit-btn') as HTMLButtonElement | null;
        const submitBtn = document.getElementById('sell-submit-btn') as HTMLButtonElement | null;
        if (formTitle) formTitle.textContent = 'Create / Update Page';
        if (cancelEditBtn) cancelEditBtn.style.display = 'none';
        if (submitBtn) submitBtn.textContent = 'Publish Sell Page';
        state.editingPageId = null;
      }
    });
  });
}

function showError(el: HTMLElement, message: string) {
  el.textContent = message;
  el.style.display = 'block';
}

function populateSellFormForEdit(page: SellPage) {
  const form = document.getElementById('sell-form') as HTMLFormElement | null;
  if (!form) return;

  state.editingPageId = page.id;
  (document.getElementById('sell-form-title') as HTMLElement).textContent = `Edit ${page.domain_name}`;
  (document.getElementById('sell-domain') as HTMLInputElement).value = page.domain_name;
  (document.getElementById('sell-price') as HTMLInputElement).value = String(page.price);
  (document.getElementById('sell-email') as HTMLInputElement).value = page.contact_email;
  (document.getElementById('sell-template') as HTMLSelectElement).value = page.template_id;
  (document.getElementById('sell-status') as HTMLSelectElement).value = page.status;
  const showStatsImage = document.getElementById('sell-show-stats-image') as HTMLInputElement;
  showStatsImage.checked = page.show_stats_image === 1;
  const imageUploadWrap = document.getElementById('sell-image-upload-wrap') as HTMLElement;
  imageUploadWrap.style.display = showStatsImage.checked ? 'block' : 'none';
  (document.getElementById('sell-cancel-edit-btn') as HTMLButtonElement).style.display = 'inline-flex';
  (document.getElementById('sell-submit-btn') as HTMLButtonElement).textContent = 'Save Changes';
}

function resetSellForm(form: HTMLFormElement, container: HTMLElement) {
  form.reset();
  const showStatsImage = container.querySelector('#sell-show-stats-image') as HTMLInputElement;
  const imageUploadWrap = container.querySelector('#sell-image-upload-wrap') as HTMLElement;
  showStatsImage.checked = false;
  imageUploadWrap.style.display = 'none';
  const uploadLoader = container.querySelector('#sell-upload-loader') as HTMLElement;
  uploadLoader.style.display = 'none';
  state.uploadingImage = false;
}
